import firebase from 'firebase/app';
import { useState, useEffect } from 'react';
import { deleteModal } from 'util/modal';
import { adminFunctionsEnabled, Role, roleRank } from 'util/adminApi';

const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<firebase.User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [role, setRole] = useState<Role | null>(null);
  const [loadingAuthState, setLoadingAuthState] = useState(true); // New state

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user);
        // Roles are a claim on the sign-in account, set only by server functions.
        const token = await user.getIdTokenResult();
        const claimRole = typeof token.claims.role === 'string' ? (token.claims.role as Role) : null;
        setRole(claimRole);
        if (claimRole) {
          setIsAdmin(roleRank(claimRole) >= roleRank('admin'));
        } else if (!adminFunctionsEnabled) {
          // Live site until Phase 1 is deployed there: the old isAdmin flag on the profile.
          const userDoc = await firebase.firestore().collection('users').doc(user.uid).get();
          setIsAdmin(userDoc.exists ? userDoc.data()?.isAdmin || false : false);
        } else {
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setRole(null);
        setIsAdmin(false);
      }
      setLoadingAuthState(false); // Auth state has been checked
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      await firebase.auth().signInWithEmailAndPassword(email, password);
      return true;
    } catch (error) {
      console.error('Sign in error:', error); // Debug log
      return false;
    }
  };

  const signOut = async () => {
    const confirm = await deleteModal('Are you sure you want to log out?');
    if (confirm) {
      firebase.auth().signOut();
    }
  };

  const signUp = async (email: string, password: string, isAdmin: boolean = false) => {
    try {
      setLoading(true);
      const userCredential = await firebase.auth().createUserWithEmailAndPassword(email, password);
      if (!userCredential || !userCredential.user) return;

      const userId = userCredential.user.uid;

      await firebase.firestore().collection('users').doc(userId).set({
        email,
        isAdmin: isAdmin,  // Store isAdmin as a boolean
      });

      alert('User added successfully');
    } catch (error) {
      console.error('Signup error:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    signIn,
    signOut,
    signUp,
    user,
    isAdmin,
    role,
    loadingAuthState, // Return the loadingAuthState
    loading,
  };
};

export default useAuth;
